import {useCallback, useEffect, useMemo, useState} from 'react';
import {API} from '@/App';
import {getSessionToken, useSessionToken} from '@/auth/session';
import communicationsGuide from '@/data/communicationsGuide.json';

export const COMMS_CHANGED = 'bds-communications-changed';
export const CATEGORIES = communicationsGuide.categories.map(({id,label})=>[id,label]);
export const categoryLabel = value => CATEGORIES.find(([key]) => key === value)?.[1] || ({data_request:'Report or data issue'}[value]) || 'Other';
export const statusLabel = value => ({open:'Open', in_progress:'Open', waiting_customer:'Waiting for your reply', resolved:'Resolved', closed:'Resolved'}[value] || value || 'Open');
export const newIntent = () => globalThis.crypto?.randomUUID?.() || `comms-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const safeLink = value => {
  if (typeof value !== 'string' || /[\u0000-\u0020\\]/.test(value)) return null;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : null; } catch { return null; }
};
export const safeAnnouncementLink=value=>{
  const link=safeLink(value);if(!link)return null;if(link.startsWith('https:'))return link;
  const parsed=new URL(link,'https://app.invalid');
  return ['overview','markets','research','monitor','ask','messages','support','settings','dealflow','catalysts','exports'].includes(parsed.pathname.split('/')[1])&&!parsed.search&&!parsed.hash?link:null;
};
export const timestamp = value => {
  if (!value || Number.isNaN(new Date(value).getTime())) return 'Date unavailable';
  return new Date(value).toLocaleString(undefined, {dateStyle:'medium',timeStyle:'short'});
};
export const notifyComms = () => window.dispatchEvent(new Event(COMMS_CHANGED));

export async function commsRequest(path, {token, signal, method='GET', body, intent, headers={}} = {}) {
  if (!token || token !== getSessionToken()) throw new Error('Sign in to view your messages.');
  const supplemental={};
  for(const name of ['X-Announcement-Version','X-Announcement-Reservation'])if(typeof headers[name]==='string'&&headers[name].length<=512&&!/[\r\n]/.test(headers[name]))supplemental[name]=headers[name];
  const response = await fetch(`${API}${path}`, {method,signal,headers:{...supplemental,Authorization:`Bearer ${token}`,...(body !== undefined ? {'Content-Type':'application/json'} : {}),...(intent ? {'Idempotency-Key':intent} : {})},...(body !== undefined ? {body:JSON.stringify(body)} : {})});
  if (token !== getSessionToken()) throw new DOMException('Account changed','AbortError');
  if (!response.ok) {
    const messages={401:'Your session expired. Sign in again.',403:'You do not have access to this conversation or action.',404:'This item is unavailable.',409:'This review is no longer current. Review the audience again.',422:'Check the fields and try again.',429:'Too many requests. Wait before trying again.',503:'Messaging is not enabled. Help articles remain available.'};
    const error=new Error(messages[response.status] || 'Could not load or save this item. Try again.');error.status=response.status;throw error;
  }
  const data=response.status === 204 ? {} : await response.json();
  if (token !== getSessionToken()) throw new DOMException('Account changed','AbortError');
  return data;
}
export function useCommsClient() {
  const token=useSessionToken();
  const controllers=useMemo(()=>new Set(),[token]);
  useEffect(()=>()=>{controllers.forEach(controller=>controller.abort());controllers.clear();},[controllers]);
  const request=useCallback(async(path,options={})=>{
    const controller=new AbortController();controllers.add(controller);
    try{return await commsRequest(path,{...options,token,signal:controller.signal});}finally{controllers.delete(controller);}
  },[token,controllers]);
  return {token,request};
}
export function useScopedResource(path, {enabled=true}={}) {
  const {token,request}=useCommsClient();
  const key=`${token || ''}:${path || ''}`;
  const [state,setState]=useState({key:null,data:null,error:null,loading:false});
  const [revision,setRevision]=useState(0);
  useEffect(()=>{
    if(!enabled || !token || !path)return;
    let active=true;
    setState({key,data:null,error:null,loading:true});
    request(path).then(data=>{if(active)setState({key,data,error:null,loading:false});}).catch(error=>{if(active && error.name !== 'AbortError')setState({key,data:null,error:error.message,errorStatus:error.status,loading:false});});
    return()=>{active=false;};
  },[enabled,token,path,key,request,revision]);
  const reload=useCallback(()=>setRevision(value=>value+1),[]);
  const visible=state.key === key && enabled && token ? state : {data:null,error:null,loading:!!(enabled&&token&&path)};
  return {...visible,reload,token,request};
}
export function useCommsConfig() {
  const resource=useScopedResource('/messages/config');
  return {...resource,enabled:resource.data?.enabled === true};
}
export function useCursorList(path,options) {
  const resource=useScopedResource(path,options);
  const key=`${resource.token}:${path}`;
  const [more,setMore]=useState({key:null,items:[],cursor:null,busy:false,error:null});
  const extra=more.key===key?more.items:[];
  const nextCursor=more.key===key?more.cursor:resource.data?.next_cursor;
  const loadMore=useCallback(async()=>{
    if(!nextCursor)return;
    setMore({key,items:extra,cursor:nextCursor,busy:true,error:null});
    try{const data=await resource.request(`${path}${path.includes('?')?'&':'?'}cursor=${encodeURIComponent(nextCursor)}`);setMore({key,items:[...extra,...(data.items||[])],cursor:data.next_cursor,busy:false,error:null});}catch(error){if(error.name!=='AbortError')setMore({key,items:extra,cursor:nextCursor,busy:false,error:error.message});}
  },[nextCursor,key,extra,resource.request,path]);
  const reload=useCallback(()=>{setMore({key:null,items:[],cursor:null,busy:false,error:null});resource.reload();},[resource.reload]);
  return {...resource,reload,items:resource.data?[...(resource.data.items||[]),...extra]:[],nextCursor,loadMore,pageBusy:more.key===key&&more.busy,pageError:more.key===key?more.error:null};
}

// Message text stays only in this signed-in session's memory, never localStorage.
let draftScope=null;
const drafts=new Map();
if(typeof window!=='undefined') {
  const clearAccountDrafts=()=>{drafts.clear();draftScope=null;};
  window.addEventListener('bds-session-changed',clearAccountDrafts);
  window.addEventListener('storage',event=>{if(event.key==='session_token'||event.key===null)clearAccountDrafts();});
}
function ensureDraftScope(scope){if(draftScope!==scope){drafts.clear();draftScope=scope;}}
export function useSessionDraft(key,scope) {
  ensureDraftScope(scope);
  const [revision,setRevision]=useState(0);
  const value=drafts.get(key) || '';
  const setValue=useCallback(next=>{ensureDraftScope(scope);drafts.set(key,next);setRevision(n=>n+1);},[key,scope]);
  const clear=useCallback(()=>{ensureDraftScope(scope);drafts.delete(key);setRevision(n=>n+1);},[key,scope]);
  useEffect(()=>{const reset=()=>{if(getSessionToken() !== scope?.split('|')[0]){drafts.clear();draftScope=null;setRevision(n=>n+1);}};window.addEventListener('bds-session-changed',reset);window.addEventListener('storage',reset);return()=>{window.removeEventListener('bds-session-changed',reset);window.removeEventListener('storage',reset);};},[scope]);
  void revision;
  return [value,setValue,clear];
}
